import { format } from 'date-fns';
import { z } from 'zod';
import { legalClientAccountingSchema } from './ILegalClientAccounting';
import { legalClientAddressSchema } from './ILegalClientAddress';
import { contactInformationSchema } from './ILegalClientContactInformation';
import { legalClientRelationshipSchema } from './ILegalClientRelationship/ILegalClientRelationship';
import {
  legalTransactionSchema,
  type ILegalTransaction,
} from './ILegalTransaction';

export interface ILegalTransactionObject {
  LegalTransaction: ILegalTransaction;
  LegalRole: string | null;
  idLegalRole: number | null;
}

export const legalTransactionObjectSchema = z.object({
  LegalTransaction: legalTransactionSchema,
  LegalRole: z.string().nullable(),
  idLegalRole: z.number().nullable(),
});

export const legalClientSchema = z.object({
  idLegalClient: z.number(),
  idCountry: z.number(),
  idBusinessForm: z.number(),
  FirstName: z.string().nullable(),
  LastName: z.string().nonempty(),
  SurName: z.string().nullable().optional(),
  Email: z.string().nullable().optional(),
  Birthdate: z.string().nullable().optional(),
  Birthplace: z.string().nullable().optional(),
  PlaceOfDeath: z.string().nullable().optional(),
  DateOfDeath: z.string().nullable().optional(),
  idGender: z.number().nullable().optional(),
  Gender: z.string().nullable().optional(),
  TaxNumber: z.string().nullable().optional(),
  SalesTaxNumber: z.string().nullable().optional(),
  idAcademicTitle: z.number().nullable().optional(),
  AcademicTitle: z.string().nullable().optional(),
  BusinessForm: z.string().nullable().optional(),
  idLegalClientType: z.number(),
  LegalClientType: z.string(),
  idLegalClientUser: z
    .number()
    .nullable()
    .transform(x => x ?? -1),
  loginPossible: z.boolean(),
  passwordResetRequired: z.boolean(),
  last_login_at: z.string().nullable().optional(),
  isReceivingEmailEnabled: z.boolean(),
  Updated_at: z.string().nullable().optional(),
  Created_at: z.string().nullable().optional(),
  syncWithArnoTop: z.boolean(),
  //LegalClientUser: legalClientUserSchema,
  idArnoTop: z.number().nullable().optional(),
  //syncWithArnotop: z.boolean().optional(),
  idContactGroup: z.number().nullable().optional(),
  ContactGroup: z.string().nullable().optional(),
  LastSyncWithArnoTop: z.string().nullable().optional(),
  idArnoTopIntern: z.number().nullable().optional(),
  idLegalRole: z.number().nullable().optional(),
  enableInvoiceReminding: z.boolean().optional(),
  enableCertificateReminding: z.boolean().optional(),

  // !!! Attr. ohne Attr. in der DB
  syncType: z.number().optional(),
  resetPassword: z.boolean().optional(),
});

export const legalClientFullObjectSchema = legalClientSchema.extend({
  AddressArray: z.array(legalClientAddressSchema),
  ContactInformationArray: z.array(contactInformationSchema),
  AccountingArray: z.array(legalClientAccountingSchema),
  LegalTransactionArray: z.array(legalTransactionObjectSchema),
  RelationArray: z.array(z.lazy(() => legalClientRelationshipSchema)),
});

export type ILegalClient = z.infer<typeof legalClientSchema>;
export type ILegalClientFullObject = z.infer<
  typeof legalClientFullObjectSchema
>;

export const initLegalClient = (): ILegalClientFullObject => {
  return {
    idLegalClient: -1,
    idCountry: 1,
    idBusinessForm: 1,
    FirstName: '',
    LastName: '',
    idGender: 4,
    SurName: '',
    Birthdate: '',
    Birthplace: '',
    idLegalClientUser: -1,
    AddressArray: [],
    ContactInformationArray: [],
    AccountingArray: [],
    LegalTransactionArray: [],
    RelationArray: [],
    idLegalClientType: 1,
    LegalClientType: '',
    loginPossible: false,
    passwordResetRequired: false,
    isReceivingEmailEnabled: false,
    syncWithArnoTop: false,
    idLegalRole: null,
  };
};

export const transferLegalClientInput = ({
  Birthdate,
  loginPossible,
  isReceivingEmailEnabled,
  passwordResetRequired,
  idContactGroup,
  syncWithArnoTop,
  idArnoTop,
  ...rest
}: ILegalClientFullObject): ILegalClientFullObject => ({
  ...rest,
  Birthdate: Birthdate ? format(new Date(Birthdate), 'yyyy-MM-dd') : undefined,
  loginPossible: Number(loginPossible) !== 0,
  isReceivingEmailEnabled: Number(isReceivingEmailEnabled) !== 0,
  passwordResetRequired: Number(passwordResetRequired) !== 0,
  idContactGroup: idContactGroup || null,
  syncWithArnoTop: syncWithArnoTop || idArnoTop ? true : false,
  RelationArray: [],
});
